// Helper puri per l'eliminazione multipla di media/file (Tab Media e File Manager),
// separati dai componenti per poterli testare senza React.

/**
 * Esegue `fn` su ogni elemento con al massimo `limit` operazioni in parallelo.
 * I risultati "settled" mantengono l'ordine di `items`; un errore su un
 * elemento non interrompe gli altri.
 */
export async function mapWithConcurrency<T, R>(
    items: T[],
    limit: number,
    fn: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
    const results: PromiseSettledResult<R>[] = new Array(items.length);
    let next = 0;
    const worker = async () => {
        // Il contatore condiviso `next` assegna ogni elemento a un solo worker.
        while (true) {
            const i = next++;
            if (i >= items.length) break;
            try {
                results[i] = { status: 'fulfilled', value: await fn(items[i]) };
            } catch (reason) {
                results[i] = { status: 'rejected', reason };
            }
        }
    };
    if (items.length === 0) return results;
    const workers = Math.max(1, Math.min(limit, items.length));
    await Promise.all(Array.from({ length: workers }, worker));
    return results;
}

export type MediaDeleteOutcome = 'deleted' | 'requested';

/**
 * Interpreta la risposta di `DELETE /media/{id}`:
 *   - `deleted` (o `direct === true`) → media eliminato subito
 *   - `created` / `already_pending`   → richiesta di approvazione creata o già esistente
 * Lo `status` può stare in `res.data.data` o direttamente in `res.data`.
 */
export function classifyMediaDeleteResponse(res: any): MediaDeleteOutcome {
    const payload = res?.data?.data ?? res?.data;
    const status: string | undefined = payload?.status;
    const direct = payload?.direct === true || status === 'deleted';
    return direct ? 'deleted' : 'requested';
}

export interface BatchDeleteSummary {
    deleted: number;
    requested: number;
    errors: number;
}

/** Compone il testo del toast riassuntivo (es. "3 eliminati · 1 in richiesta di approvazione"). */
export function summarizeBatchDelete(s: BatchDeleteSummary): string {
    const parts: string[] = [];
    if (s.deleted) parts.push(`${s.deleted} eliminat${s.deleted === 1 ? 'o' : 'i'}`);
    if (s.requested) parts.push(`${s.requested} in richiesta di approvazione`);
    if (s.errors) parts.push(`${s.errors} error${s.errors === 1 ? 'e' : 'i'}`);
    return parts.join(' · ') || 'Nessuna operazione';
}
